import React, {useEffect, useRef, useState} from 'react';
import {
    Box, Typography, Paper, Chip,
    FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText
} from '@mui/material';
import ReconnectingEventSource from 'reconnecting-eventsource';

// Shared LogsCard used by GameDetailPage and MachineDetailPage. Streams Cloud
// Logging entries over SSE, with a navigation cache so re-mounting an entity
// you just looked at hydrates instantly and the SSE only backfills what's newer.

const SEVERITY_COLOR = {
    DEBUG: 'default', INFO: 'info', NOTICE: 'info', WARNING: 'warning',
    ERROR: 'error', CRITICAL: 'error', ALERT: 'error', EMERGENCY: 'error',
};
const ALL_SEVERITIES = ['DEBUG', 'INFO', 'NOTICE', 'WARNING', 'ERROR', 'CRITICAL'];

// Service identity comes from different places depending on the workload:
//  1. Spring Boot apps (game-server, claw-machine-server, socket-io-server, video-record,
//     admin-service, ...) emit `jsonPayload.serviceContext.service` — the canonical service id.
//  2. Cloud Run exposes `resource.labels.serviceName`.
//  3. MIG VMs sometimes expose `resource.labels.instanceName` (e.g. `game-server-instance-prod-abc`)
//     — trim to the leading service token.
//  4. Last resort: the bare resource type (`gce_instance` / `cloud_run_revision`).
// Note: GCE `resource.labels` only carries instance_id (numeric) by default — that's why we have
// to look in jsonPayload for a human-readable name.
const serviceOf = ({jsonPayload, resource}) => {
    const l = resource?.labels || {};
    return jsonPayload?.serviceContext?.service
        || l.serviceName
        || l.instanceName?.split('-instance-')[0]
        || resource?.type
        || '?';
};

// Spring/spring-cloud-gcp-logging puts the SLF4J logger FQCN in jsonPayload.logger
// (e.g. com.nexalgames.liveclaw.service.queue.QueueManager). Show only the simple
// class name — full package is noise in a tight log panel.
const loggerOf = ({jsonPayload}) => {
    const fqcn = jsonPayload?.logger;
    if (!fqcn) return '';
    const idx = fqcn.lastIndexOf('.');
    return idx >= 0 ? fqcn.substring(idx + 1) : fqcn;
};

const messageOf = ({textPayload, jsonPayload}) =>
    textPayload || jsonPayload?.message || (jsonPayload && JSON.stringify(jsonPayload)) || '';

// Module-scope cache: cacheKey → { entries, lastTimestamp }. Survives unmount/
// remount during a session. Shared across entity types — caller picks a
// namespaced key like "game:<uuid>" or "machine:<uuid>" to avoid collisions.
// Cleared on full page reload — admin shouldn't carry stale logs across sessions.
const logsCache = new Map();
const MAX_CACHED_ENTITIES = 40;

const cachePut = (key, entries, lastTimestamp) => {
    // Trim oldest when over budget (insertion-ordered Map iteration → first is oldest).
    while (logsCache.size >= MAX_CACHED_ENTITIES && !logsCache.has(key)) {
        logsCache.delete(logsCache.keys().next().value);
    }
    logsCache.set(key, {entries, lastTimestamp});
};

/**
 * @param {string} title       Header text (e.g. "Game Logs", "Machine Logs").
 * @param {string} streamUrl   SSE endpoint, e.g. `/api/games/<id>/logs/stream`.
 *                             A `?since=<iso>` param is appended automatically
 *                             when we have a resume cursor from the cache.
 * @param {string} cacheKey    Stable per-entity key for the nav cache, e.g.
 *                             `game:<uuid>`. Empty string disables caching.
 * @param {string} emptyText   Optional override for the "no entries" message.
 */
const LogsCard = ({title, streamUrl, cacheKey, emptyText}) => {
    // Hydrate from cache synchronously so the first render already shows cached
    // entries — no flash of "Loading…" on re-navigation to a recently-viewed entity.
    const cached = cacheKey ? logsCache.get(cacheKey) : null;
    const [entries, setEntries] = useState(cached?.entries ?? []);
    const [historyDone, setHistoryDone] = useState(false);
    const [ended, setEnded] = useState(false);
    const [warning, setWarning] = useState(null);
    const [selectedSeverities, setSelectedSeverities] = useState(ALL_SEVERITIES);
    const containerRef = useRef(null);
    // Mirror of state for the cleanup writeback — closures captured by the
    // effect see this without re-running the effect on every render.
    const entriesRef = useRef(entries);
    entriesRef.current = entries;

    useEffect(() => {
        if (!streamUrl) return;
        const seed = cacheKey ? logsCache.get(cacheKey) : null;
        setEntries(seed?.entries ?? []);
        setHistoryDone(false); setEnded(false); setWarning(null);

        // ?since=<lastTimestamp> tells the backend to only return entries
        // strictly newer than what the client already has cached.
        const sep = streamUrl.includes('?') ? '&' : '?';
        const url = seed?.lastTimestamp
            ? `${streamUrl}${sep}since=${encodeURIComponent(seed.lastTimestamp)}`
            : streamUrl;
        const es = new ReconnectingEventSource(url, {
            withCredentials: true, max_retry_time: 1000,
        });
        let closed = false;
        es.onopen = () => setWarning(null);
        es.onerror = () => { if (!closed) setWarning('Log stream connection lost; retrying…'); };

        const addEntry = (e) => {
            try {
                const entry = JSON.parse(e.data);
                setEntries(prev => prev.length >= 5000 ? prev : [...prev, entry]);
            } catch (err) { console.warn('Bad SSE payload', err); }
        };
        const handleHistoricalDone = () => setHistoryDone(true);
        const handleEnd = () => { closed = true; setEnded(true); es.close(); };
        const handleWarning = (e) => setWarning(e.data);
        // Backend signals terminal stream failures via a `stream-error` event
        // instead of dropping the connection — that way we don't loop on
        // ReconnectingEventSource for unrecoverable errors. The
        // `tail-quota-exhausted` code (RESOURCE_EXHAUSTED) is the big one:
        // Cloud Logging caps concurrent tail sessions per resource and the
        // backend can't recover from it server-side. Stop reconnecting and
        // surface a clear message.
        const handleStreamError = (e) => {
            const payload = e.data || '';
            closed = true;
            es.close();
            if (payload.startsWith('tail-quota-exhausted')) {
                setEnded(true);
                setWarning('Cloud Logging tail-quota reached. Close other admin tabs viewing this entity and reload to retry.');
            } else {
                setWarning(`Log stream error: ${payload}`);
            }
        };

        es.addEventListener('log-entry', addEntry);
        es.addEventListener('historical-done', handleHistoricalDone);
        es.addEventListener('end', handleEnd);
        es.addEventListener('warning', handleWarning);
        es.addEventListener('stream-error', handleStreamError);

        return () => {
            closed = true;
            es.close();
            // Snapshot to cache on unmount so the next mount can pick up from
            // the latest entry's timestamp.
            const snap = entriesRef.current;
            if (cacheKey && snap.length > 0) {
                const last = snap[snap.length - 1].timestamp;
                cachePut(cacheKey, snap, last);
            }
        };
    }, [streamUrl, cacheKey]);

    useEffect(() => {
        const el = containerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [entries]);

    // Filter happens client-side — backend always streams everything. Default
    // severity for entries without explicit severity is INFO (Cloud Logging convention).
    const visibleEntries = entries.filter(e =>
        selectedSeverities.includes(e.severity || 'INFO')
    );

    // Status chip: "Loading history…" until backend signals historical-done,
    // then "Live tail (N visible / M total)" while the SSE stream stays open.
    // `ended` only fires for special cases (game-not-started, tail-completed).
    const visibleVsTotal = visibleEntries.length === entries.length
        ? `${entries.length}`
        : `${visibleEntries.length} / ${entries.length}`;
    const statusLabel = ended
        ? `End of logs (${visibleVsTotal})`
        : !historyDone ? 'Loading history…' : `Live tail (${visibleVsTotal})`;
    const statusColor = ended ? 'default' : !historyDone ? 'default' : 'success';

    const emptyMsg = entries.length === 0
        ? (historyDone ? (emptyText || 'No log entries found.') : 'Loading logs from Cloud Logging…')
        : `All ${entries.length} entries filtered out by severity.`;

    return (
        <Paper elevation={2} sx={{p: 2, display: 'flex', flexDirection: 'column'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap'}}>
                <Typography variant="h6">{title}</Typography>
                <Chip label={statusLabel} size="small" color={statusColor}/>
                <Box sx={{flexGrow: 1}}/>
                <FormControl size="small" sx={{minWidth: 200}}>
                    <InputLabel id={`${title}-severity-label`}>Severity</InputLabel>
                    <Select
                        labelId={`${title}-severity-label`}
                        multiple
                        value={selectedSeverities}
                        onChange={(e) => setSelectedSeverities(
                            // Coerce comma-string fallback into array (MUI quirk).
                            typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                        )}
                        renderValue={(sel) =>
                            sel.length === ALL_SEVERITIES.length ? 'All' :
                            sel.length === 0 ? 'None' : sel.join(', ')
                        }
                        label="Severity"
                    >
                        {ALL_SEVERITIES.map(sev => (
                            <MenuItem key={sev} value={sev}>
                                <Checkbox checked={selectedSeverities.includes(sev)}/>
                                <ListItemText primary={sev}/>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            {warning && <Typography color="warning.main" variant="caption">{warning}</Typography>}
            <Box ref={containerRef} sx={{
                flexGrow: 1, overflowY: 'auto', maxHeight: 600,
                border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1,
                fontFamily: 'monospace', fontSize: '0.75rem',
                bgcolor: 'grey.900', color: 'grey.100',
            }}>
                {visibleEntries.length === 0 ? (
                    <Typography variant="caption" sx={{color: 'grey.500'}}>{emptyMsg}</Typography>
                ) : visibleEntries.map((entry, i) => (
                    <Box key={i} sx={{display: 'flex', gap: 1, py: 0.25}}>
                        <Box sx={{color: 'grey.500', whiteSpace: 'nowrap'}}>
                            {entry.timestamp?.slice(11, 23) || ''}
                        </Box>
                        <Chip
                            label={entry.severity || 'INFO'}
                            size="small"
                            color={SEVERITY_COLOR[entry.severity] || 'default'}
                            sx={{height: 18, fontSize: '0.65rem', minWidth: 70}}
                        />
                        <Box sx={{color: 'cyan', whiteSpace: 'nowrap'}}>{serviceOf(entry)}</Box>
                        <Box sx={{color: 'lightgreen', whiteSpace: 'nowrap'}}>{loggerOf(entry)}</Box>
                        <Box sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', flex: 1}}>{messageOf(entry)}</Box>
                    </Box>
                ))}
            </Box>
        </Paper>
    );
};

export default LogsCard;
